import React from "react";

import getEnums from "$/settings/enums";
import PAID_FEATURES from "$/settings/paid-features.json";
import { useAuth } from "~/components/general";
import {
  customFieldTypes,
  customFieldWorkOrderScopes,
} from "~/constants/filters/customFieldTypes";
import {
  ASSIGNEE,
  AssigneeFilterObj,
  DATE_RANGE,
  DateRangeFilterObj,
  FACILITY_NAME,
  getFacilityFilterObj,
  TeamsFilterObj,
  SCHEDULED,
  ScheduleFilterObj,
  TEAMS,
  TICKET_STATUS,
  TICKET_TYPE,
  TicketStatusFilterObj,
  TicketTypeFilterObj,
  ASSET_ASSIGNED,
  ASSET_NAME,
  ASSET_QR_CODE_ACCESS,
  ASSET_TYPE,
  AssetAssignedFilterObj,
  AssetFilterObj,
  AssetQRAccessFilterObj,
  AssetTypeFilterObj,
  CreatedByFilterObj,
  CREATED_BY,
  DueDateFilterObj,
  DUE_DATE,
  CONNECTION_NAME,
  ConnectionTypeFilterObj,
  CONNECTION_TYPE,
  VISIBILITY,
  VisibilityFilterObj,
  AuthorFilterObj,
  AUTHOR,
  QUOTE_AMOUNT,
  QUOTE_DATE,
  QUOTE_STATUS,
  QUOTE_VALID_UNTIL,
  QUOTE_WORK_ORDER,
  INVOICE_AMOUNT,
  INVOICE_DATE,
  INVOICE_DUE_DATE,
  INVOICE_ISSUE_DATE,
  INVOICE_STATUS,
  INVOICE_WORK_ORDER,
} from "~/constants/filters/filters";
import {
  financeQuotesDefaultFilters,
  QuoteAmountFilterObj,
  QuoteDateFilterObj,
  QuoteStatusFilterObj,
  QuoteValidUntilFilterObj,
  QuoteWorkOrderFilterObj,
} from "~/constants/filters/financeQuotesFilters";
import {
  financeInvoicesDefaultFilters,
  InvoiceAmountFilterObj,
  InvoiceDateFilterObj,
  InvoiceDueDateFilterObj,
  InvoiceIssueDateFilterObj,
  InvoiceStatusFilterObj,
  InvoiceWorkOrderFilterObj,
} from "~/constants/filters/financeInvoicesFilters";
import {
  contactsDefaultFilters,
  ContactCompanyFilterObj,
  ContactCreatedDateFilterObj,
  CONTACT_COMPANY,
  CONTACT_CREATED_DATE,
} from "~/constants/filters/contactsFilters";
import {
  inventoryStockDefaultFilters,
  AvailableStockFilterObj,
  ConsumedStockFilterObj,
  PartPriceFilterObj,
  AVAILABLE_STOCK,
  CONSUMED_STOCK,
  PART_PRICE,
} from "~/constants/filters/inventoryFilters";
import {
  ARCHIVED_WORK_ORDER,
  MACHINES,
  MACHINES_FACILITY,
  MACHINES_TEAM,
  MY_WORK_ORDER,
  WORK_ORDER,
  SUPPORT_TICKET,
  LINK_WORK_ORDER,
  WORK_ORDER_SCHEDULER,
  KNOWLEDGE_BASE,
  PME,
  ASSET_TEMPLATE,
  FACILITES,
  FINANCE_QUOTES,
  FINANCE_INVOICES,
  CONTACTS,
  INVENTORY,
  PARTS,
} from "~/constants/filters/scopes";
import { useListOwnOemCustomFields } from "~/services";
import { isMongoDBId } from "~/utils";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

const paidFeatures = getEnums(PAID_FEATURES, "reference");

const SELECTED_FILTERS_SUFIX = "_SELECTED_FILTERS";
const ACTIVE_FILTERS_SUFIX = "_ACTIVE_FILTERS";

const getValidActiveFilters = (filters, allCustomFields) => {
  const validFilterIds = allCustomFields.map((e) => e._id);
  const validFilters = {};
  for (const filterKey of Object.keys(filters)) {
    if (validFilterIds.includes(filterKey) || !isMongoDBId(filterKey)) {
      validFilters[filterKey] = filters[filterKey];
    }
  }
  return validFilters;
};

const getValidSelectedFilters = (filters, allCustomFields) => {
  const validFilterIds = allCustomFields.map((e) => e._id);
  const validFilters = [];
  for (const filterKey of filters) {
    if (validFilterIds.includes(filterKey) || !isMongoDBId(filterKey)) {
      validFilters.push(filterKey);
    }
  }
  return validFilters;
};

const getDefaultFilters = (
  scope = WORK_ORDER,
  isTeamsPaid = false,
  isInventoryStockPaid = false,
) => {
  switch (scope) {
    case WORK_ORDER:
    case LINK_WORK_ORDER:
    case WORK_ORDER_SCHEDULER:
    case MY_WORK_ORDER:
      return [
        TicketStatusFilterObj,
        AssigneeFilterObj,
        ScheduleFilterObj,
        TicketTypeFilterObj,
        getFacilityFilterObj(),
        AssetFilterObj,
        DateRangeFilterObj,
        ...(isTeamsPaid ? [TeamsFilterObj] : []),
      ];

    // Support tickets reuse the ticket filter UI but the backend has no
    // scheduled/date-range equivalent, so those work-order filters are omitted.
    case SUPPORT_TICKET:
      return [
        TicketStatusFilterObj,
        AssigneeFilterObj,
        TicketTypeFilterObj,
        getFacilityFilterObj(),
        AssetFilterObj,
        ...(isTeamsPaid ? [TeamsFilterObj] : []),
      ];

    // archiving a work order always closes it, so a status filter would only
    // ever have one value to offer here
    case ARCHIVED_WORK_ORDER:
      return [
        AssigneeFilterObj,
        ScheduleFilterObj,
        TicketTypeFilterObj,
        getFacilityFilterObj(),
        AssetFilterObj,
        DateRangeFilterObj,
        ...(isTeamsPaid ? [TeamsFilterObj] : []),
      ];

    case MACHINES:
    case MACHINES_FACILITY:
    case MACHINES_TEAM:
      return [
        AssetAssignedFilterObj,
        getFacilityFilterObj(),
        AssetTypeFilterObj,
        ...(isTeamsPaid ? [TeamsFilterObj] : []),
      ];

    case FACILITES:
      return [
        ConnectionTypeFilterObj,
        ...(isTeamsPaid ? [TeamsFilterObj] : []),
      ];

    case PME:
      return [
        AssetFilterObj,
        getFacilityFilterObj(),
        CreatedByFilterObj,
        DueDateFilterObj,
      ];

    case ASSET_TEMPLATE:
      // Products: sorting only — no filter chips
      return [];

    case KNOWLEDGE_BASE:
      return [AuthorFilterObj];

    case FINANCE_QUOTES:
      return financeQuotesDefaultFilters;

    case FINANCE_INVOICES:
      return financeInvoicesDefaultFilters;

    case CONTACTS:
      return contactsDefaultFilters;

    case INVENTORY:
    case PARTS:
      return isInventoryStockPaid ? inventoryStockDefaultFilters : [];

    default:
      return [];
  }
};

const getInitialActiveFilters = (scope = WORK_ORDER) => {
  switch (scope) {
    case WORK_ORDER:
    case LINK_WORK_ORDER:
    case WORK_ORDER_SCHEDULER:
    case MY_WORK_ORDER:
      return {
        [TICKET_STATUS]: [],
        [ASSIGNEE]: [],
        [TICKET_TYPE]: [],
        [CONNECTION_NAME]: [],
        [ASSET_NAME]: [],
        [DATE_RANGE]: [],
        [SCHEDULED]: [],
        [TEAMS]: [],
      };

    case SUPPORT_TICKET:
      return {
        [TICKET_STATUS]: [],
        [ASSIGNEE]: [],
        [TICKET_TYPE]: [],
        [CONNECTION_NAME]: [],
        [ASSET_NAME]: [],
        [TEAMS]: [],
      };

    case ARCHIVED_WORK_ORDER:
      return {
        [ASSIGNEE]: [],
        [TICKET_TYPE]: [],
        [CONNECTION_NAME]: [],
        [ASSET_NAME]: [],
        [DATE_RANGE]: [],
        [SCHEDULED]: [],
        [TEAMS]: [],
      };

    case MACHINES:
    case MACHINES_FACILITY:
    case MACHINES_TEAM:
      return {
        [ASSET_ASSIGNED]: [],
        [CONNECTION_NAME]: [],
        [ASSET_QR_CODE_ACCESS]: [],
        [ASSET_TYPE]: [],
        [TEAMS]: [],
      };

    case FACILITES:
      return {
        [ASSET_ASSIGNED]: [],
        [CONNECTION_TYPE]: [],
        [TEAMS]: [],
      };

    case PME:
      return {
        [ASSET_NAME]: [],
        [CONNECTION_NAME]: [],
        [CREATED_BY]: [],
        [DUE_DATE]: [],
      };

    case ASSET_TEMPLATE:
      return {};

    case KNOWLEDGE_BASE:
      return {
        [AUTHOR]: [],
      };

    case FINANCE_QUOTES:
      return {
        [QUOTE_STATUS]: [],
        [CONNECTION_NAME]: [],
        [QUOTE_DATE]: [],
        [DATE_RANGE]: [],
        [QUOTE_AMOUNT]: [],
        [QUOTE_VALID_UNTIL]: [],
        [CREATED_BY]: [],
        [QUOTE_WORK_ORDER]: [],
      };

    case FINANCE_INVOICES:
      return {
        [INVOICE_STATUS]: [],
        [CONNECTION_NAME]: [],
        [INVOICE_DATE]: [],
        [DATE_RANGE]: [],
        [INVOICE_AMOUNT]: [],
        [INVOICE_ISSUE_DATE]: [],
        [INVOICE_DUE_DATE]: [],
        [CREATED_BY]: [],
        [INVOICE_WORK_ORDER]: [],
      };

    case CONTACTS:
      return {
        [CONTACT_CREATED_DATE]: [],
        [CONTACT_COMPANY]: [],
      };

    case INVENTORY:
    case PARTS:
      return {
        [AVAILABLE_STOCK]: [],
        [CONSUMED_STOCK]: [],
        [PART_PRICE]: [],
      };

    default:
      return {};
  }
};

const getInitialToggleFilters = (scope = WORK_ORDER) => {
  switch (scope) {
    case WORK_ORDER:
    case LINK_WORK_ORDER:
    case WORK_ORDER_SCHEDULER:
    case MY_WORK_ORDER:
      return {
        [TICKET_STATUS]: {
          open: false,
        },
        [ASSIGNEE]: {
          open: false,
        },
        [TICKET_TYPE]: {
          open: false,
        },
        [CONNECTION_NAME]: {
          open: false,
        },
        [ASSET_NAME]: { open: false },
        [DATE_RANGE]: {
          open: false,
        },
        [SCHEDULED]: {
          open: false,
        },
        [TEAMS]: {
          open: false,
        },
      };

    case SUPPORT_TICKET:
      return {
        [TICKET_STATUS]: { open: false },
        [ASSIGNEE]: { open: false },
        [TICKET_TYPE]: { open: false },
        [CONNECTION_NAME]: { open: false },
        [ASSET_NAME]: { open: false },
        [TEAMS]: { open: false },
      };

    case ARCHIVED_WORK_ORDER:
      return {
        [ASSIGNEE]: {
          open: false,
        },
        [TICKET_TYPE]: {
          open: false,
        },
        [CONNECTION_NAME]: {
          open: false,
        },
        [ASSET_NAME]: { open: false },
        [DATE_RANGE]: {
          open: false,
        },
        [SCHEDULED]: {
          open: false,
        },
        [TEAMS]: {
          open: false,
        },
      };

    case MACHINES:
    case MACHINES_FACILITY:
    case MACHINES_TEAM:
      return {
        [ASSET_ASSIGNED]: { open: false },
        [CONNECTION_NAME]: {
          open: false,
        },
        [ASSET_QR_CODE_ACCESS]: { open: false },
        [ASSET_TYPE]: { open: false },
        [TEAMS]: {
          open: false,
        },
      };

    case FACILITES:
      return {
        [ASSET_ASSIGNED]: { open: false },
        [TEAMS]: {
          open: false,
        },
        [CONNECTION_TYPE]: {
          open: false,
        },
      };

    case PME:
      return {
        [ASSET_NAME]: { open: false },
        [CONNECTION_NAME]: {
          open: false,
        },
        [CREATED_BY]: { open: false },
        [DUE_DATE]: { open: false },
      };

    case ASSET_TEMPLATE:
      return {};

    case KNOWLEDGE_BASE:
      return {
        [AUTHOR]: { open: false },
      };

    case FINANCE_QUOTES:
      return {
        [QUOTE_STATUS]: { open: false },
        [CONNECTION_NAME]: { open: false },
        [QUOTE_DATE]: { open: false },
        [DATE_RANGE]: { open: false },
        [QUOTE_AMOUNT]: { open: false },
        [QUOTE_VALID_UNTIL]: { open: false },
        [CREATED_BY]: { open: false },
        [QUOTE_WORK_ORDER]: { open: false },
      };

    case FINANCE_INVOICES:
      return {
        [INVOICE_STATUS]: { open: false },
        [CONNECTION_NAME]: { open: false },
        [INVOICE_DATE]: { open: false },
        [DATE_RANGE]: { open: false },
        [INVOICE_AMOUNT]: { open: false },
        [INVOICE_ISSUE_DATE]: { open: false },
        [INVOICE_DUE_DATE]: { open: false },
        [CREATED_BY]: { open: false },
        [INVOICE_WORK_ORDER]: { open: false },
      };

    case CONTACTS:
      return {
        [CONTACT_CREATED_DATE]: { open: false },
        [CONTACT_COMPANY]: { open: false },
      };

    case INVENTORY:
    case PARTS:
      return {
        [AVAILABLE_STOCK]: { open: false },
        [CONSUMED_STOCK]: { open: false },
        [PART_PRICE]: { open: false },
      };

    default:
      return {};
  }
};

const determineFilterObj = (id) => {
  switch (id) {
    case TICKET_STATUS:
      return TicketStatusFilterObj;
    case ASSIGNEE:
      return AssigneeFilterObj;
    case SCHEDULED:
      return ScheduleFilterObj;
    case TICKET_TYPE:
      return TicketTypeFilterObj;
    case FACILITY_NAME:
      return getFacilityFilterObj();
    case CONNECTION_NAME:
      return getFacilityFilterObj();
    case DATE_RANGE:
      return DateRangeFilterObj;
    case TEAMS:
      return TeamsFilterObj;
    case CONNECTION_TYPE:
      return ConnectionTypeFilterObj;
    case ASSET_NAME:
      return AssetFilterObj;
    case ASSET_ASSIGNED:
      return AssetAssignedFilterObj;
    case ASSET_QR_CODE_ACCESS:
      return AssetQRAccessFilterObj;
    case ASSET_TYPE:
      return AssetTypeFilterObj;
    case CREATED_BY:
      return CreatedByFilterObj;
    case DUE_DATE:
      return DueDateFilterObj;
    case VISIBILITY:
      return VisibilityFilterObj;
    case AUTHOR:
      return AuthorFilterObj;
    case QUOTE_STATUS:
      return QuoteStatusFilterObj;
    case QUOTE_DATE:
      return QuoteDateFilterObj;
    case QUOTE_WORK_ORDER:
      return QuoteWorkOrderFilterObj;
    case QUOTE_AMOUNT:
      return QuoteAmountFilterObj;
    case QUOTE_VALID_UNTIL:
      return QuoteValidUntilFilterObj;
    case INVOICE_STATUS:
      return InvoiceStatusFilterObj;
    case INVOICE_DATE:
      return InvoiceDateFilterObj;
    case INVOICE_WORK_ORDER:
      return InvoiceWorkOrderFilterObj;
    case INVOICE_AMOUNT:
      return InvoiceAmountFilterObj;
    case INVOICE_ISSUE_DATE:
      return InvoiceIssueDateFilterObj;
    case INVOICE_DUE_DATE:
      return InvoiceDueDateFilterObj;
    case CONTACT_CREATED_DATE:
      return ContactCreatedDateFilterObj;
    case CONTACT_COMPANY:
      return ContactCompanyFilterObj;
    case AVAILABLE_STOCK:
      return AvailableStockFilterObj;
    case CONSUMED_STOCK:
      return ConsumedStockFilterObj;
    case PART_PRICE:
      return PartPriceFilterObj;
    default:
      return null;
  }
};

const useFilter = ({
  scope = WORK_ORDER,
  baseFilters = null,
  customFieldsFilterScope = Object.values(customFieldWorkOrderScopes),
  persistFilters = true,
}) => {
  const { user } = useAuth();
  const appConfig = useGetAppConfigValue();
  const isTeamsPaid = isPaidFeatureAvailable(
    paidFeatures.teams,
    user,
    appConfig,
  );
  const isInventoryStockPaid = isPaidFeatureAvailable(
    paidFeatures.inventoryStockManagement,
    user,
    appConfig,
  );
  const selectedFilterKey = React.useMemo(
    () => scope + SELECTED_FILTERS_SUFIX,
    [],
  );
  const activeFilterKey = React.useMemo(() => scope + ACTIVE_FILTERS_SUFIX, []);

  const [selectedFilters, setSelectedFilters] = React.useState({ value: [] });
  const [unselectedFilters, setUnselectedFilters] = React.useState(() => {
    if (!baseFilters) {
      baseFilters = getDefaultFilters(scope, isTeamsPaid, isInventoryStockPaid);
    }
    return {
      value: baseFilters,
    };
  });
  const [activeFilters, setActiveFilters] = React.useState(() =>
    getInitialActiveFilters(scope),
  );

  const [toggleFilters, setToggleFilters] = React.useState(() =>
    getInitialToggleFilters(scope),
  );

  const {
    customAdditionalFields = [],
    loading: customAdditionalFieldsLoading,
  } = useListOwnOemCustomFields();

  const machinesCustomAdditionalFields = [];
  const facilitiesCustomAdditionalFields = [];
  const ticketsCustomAdditionalFields = [];
  const supportTicketsCustomAdditionalFields = [];
  const knowledgeBaseCustomAdditionalFields = [];
  const partsCustomAdditionalFields = [];
  const contactsCustomAdditionalFields = [];

  customAdditionalFields?.forEach((field) => {
    if (field.type === customFieldTypes.ticket) {
      ticketsCustomAdditionalFields.push(field);
    }
    if (field.type === customFieldTypes.supportTicket) {
      supportTicketsCustomAdditionalFields.push(field);
    }
    if (field.type === customFieldTypes.facilities) {
      facilitiesCustomAdditionalFields.push(field);
    }
    if (field.type === customFieldTypes.machines) {
      machinesCustomAdditionalFields.push(field);
    }
    if (field.type === customFieldTypes.knowledgeBase) {
      knowledgeBaseCustomAdditionalFields.push(field);
    }
    if (field.type === customFieldTypes.parts) {
      partsCustomAdditionalFields.push(field);
    }
    if (field.type === customFieldTypes.contacts) {
      contactsCustomAdditionalFields.push(field);
    }
  });

  const updateSelectedFilters = (customFieldFilters) => {
    const selectedFilterIds = getValidSelectedFilters(
      persistFilters
        ? JSON.parse(localStorage.getItem(selectedFilterKey) || "[]")
        : [],
      customAdditionalFields,
    );

    if (selectedFilterIds.length > 0) {
      const newSelectedFilters = [];

      for (const id of selectedFilterIds) {
        const filterObj =
          determineFilterObj(id) ||
          customFieldFilters.find((i) => i._id === id);

        if (filterObj) {
          newSelectedFilters.push(filterObj);
        }
      }

      if (newSelectedFilters.length > 0) {
        setSelectedFilters(() => ({ value: newSelectedFilters }));
      }
    }
    return selectedFilterIds;
  };

  const updateUnselectedFilters = (
    allCustomFieldFilters,
    selectedFilterIds,
  ) => {
    let newUnselectedFilters = [
      ...(unselectedFilters?.value || []),
      ...allCustomFieldFilters,
    ];

    if (selectedFilterIds.length > 0) {
      newUnselectedFilters = newUnselectedFilters.filter(
        (filter) => !selectedFilterIds.includes(filter._id || filter.id),
      );
    }

    setUnselectedFilters({
      value: newUnselectedFilters,
    });
  };

  const updateActiveFilters = (allCustomFieldFilters) => {
    const active = {};
    const persistentActiveFilters = persistFilters
      ? JSON.parse(localStorage.getItem(activeFilterKey) ?? "{}")
      : {};

    allCustomFieldFilters.forEach((e) => {
      active[e._id] = [];
    });

    const newActiveFilters = {
      ...activeFilters,
      ...active,
      ...getValidActiveFilters(persistentActiveFilters, customAdditionalFields),
    };
    setActiveFilters(newActiveFilters);
  };

  const updateDropdownToggles = (allCustomFieldFilters) => {
    const dropdownToggle = {};

    allCustomFieldFilters.forEach((e) => {
      dropdownToggle[e._id] = { open: false };
    });

    setToggleFilters((prev) => ({ ...prev, ...dropdownToggle }));
  };

  const handleSelectFilter = (value) => {
    let filterIds = [];

    for (const item of value.value) {
      filterIds.push(item._id ?? item.id);
    }

    if (persistFilters) {
      localStorage.setItem(selectedFilterKey, JSON.stringify(filterIds));
    }
    setSelectedFilters(value);
  };

  const handleUnselectFilter = (value) => {
    setUnselectedFilters(value);
  };

  const handleActiveFilter = (activeFilter) => {
    const value = {};

    for (const id of Object.keys(activeFilter)) {
      if (
        activeFilter[id].length > 0 ||
        activeFilter[id].value?.length > 0 ||
        typeof activeFilter[id] === "boolean"
      ) {
        value[id] = activeFilter[id];
      }
    }

    if (persistFilters) {
      localStorage.setItem(activeFilterKey, JSON.stringify(value));
    }
    setActiveFilters(activeFilter);
  };

  /**
   * Restore both which filter chips are shown (selectedIds) and their values
   * (active) from a saved custom view.
   */
  const applyViewFilters = (viewFilters = {}) => {
    const active = viewFilters?.active || {};
    const selectedIds = [
      ...new Set([
        ...(viewFilters?.selectedIds || []),
        ...Object.keys(active).filter((id) => {
          const v = active[id];
          return (
            (Array.isArray(v) && v.length > 0) ||
            v?.value?.length > 0 ||
            typeof v === "boolean"
          );
        }),
      ]),
    ];

    const defaultFilters = getDefaultFilters(
      scope,
      isTeamsPaid,
      isInventoryStockPaid,
    );
    const allCustomFieldFilters = getCustomFieldFilters() || [];
    const catalog = [...defaultFilters, ...allCustomFieldFilters];

    const selected = [];
    for (const id of selectedIds) {
      const filterObj =
        determineFilterObj(id) || catalog.find((f) => (f.id ?? f._id) === id);
      if (filterObj) selected.push(filterObj);
    }

    const selectedIdSet = new Set(
      selected.map((f) => f.id ?? f._id).filter(Boolean),
    );
    const unselected = catalog.filter((f) => !selectedIdSet.has(f.id ?? f._id));

    const nextActive = {
      ...getInitialActiveFilters(scope),
      ...Object.fromEntries(allCustomFieldFilters.map((f) => [f._id, []])),
      ...active,
    };

    if (persistFilters) {
      localStorage.setItem(
        selectedFilterKey,
        JSON.stringify([...selectedIdSet]),
      );
      localStorage.setItem(activeFilterKey, JSON.stringify(active));
    }

    setSelectedFilters({ value: selected });
    setUnselectedFilters({ value: unselected });
    setActiveFilters(nextActive);
  };

  const getCustomFieldFilters = () => {
    const filteredFacilitiesCustomAdditionalFields =
      facilitiesCustomAdditionalFields?.filter(
        (field) => field?.fieldType !== "address",
      );

    if (
      (machinesCustomAdditionalFields.length > 0 ||
        filteredFacilitiesCustomAdditionalFields.length > 0 ||
        ticketsCustomAdditionalFields.length > 0 ||
        supportTicketsCustomAdditionalFields.length > 0 ||
        knowledgeBaseCustomAdditionalFields.length > 0 ||
        partsCustomAdditionalFields.length > 0 ||
        contactsCustomAdditionalFields.length > 0) &&
      !customAdditionalFieldsLoading
    ) {
      const machineCF = Array.isArray(machinesCustomAdditionalFields)
        ? machinesCustomAdditionalFields
        : [];
      const facilityCF = Array.isArray(filteredFacilitiesCustomAdditionalFields)
        ? filteredFacilitiesCustomAdditionalFields
        : [];
      const ticketCF = Array.isArray(ticketsCustomAdditionalFields)
        ? ticketsCustomAdditionalFields
        : [];
      const supportTicketCF = Array.isArray(
        supportTicketsCustomAdditionalFields,
      )
        ? supportTicketsCustomAdditionalFields
        : [];
      const knowledgeBaseCF = Array.isArray(knowledgeBaseCustomAdditionalFields)
        ? knowledgeBaseCustomAdditionalFields
        : [];
      const partsCF = Array.isArray(partsCustomAdditionalFields)
        ? partsCustomAdditionalFields
        : [];
      const contactsCF = Array.isArray(contactsCustomAdditionalFields)
        ? contactsCustomAdditionalFields
        : [];

      let allCustomFieldFilters = [];
      if (customFieldsFilterScope.includes(customFieldTypes.machines)) {
        allCustomFieldFilters = [...allCustomFieldFilters, ...machineCF];
      }
      if (customFieldsFilterScope.includes(customFieldTypes.facilities)) {
        allCustomFieldFilters = [...allCustomFieldFilters, ...facilityCF];
      }
      if (customFieldsFilterScope.includes(customFieldTypes.ticket)) {
        allCustomFieldFilters = [...allCustomFieldFilters, ...ticketCF];
      }
      if (customFieldsFilterScope.includes(customFieldTypes.supportTicket)) {
        allCustomFieldFilters = [...allCustomFieldFilters, ...supportTicketCF];
      }
      if (customFieldsFilterScope.includes(customFieldTypes.knowledgeBase)) {
        allCustomFieldFilters = [...allCustomFieldFilters, ...knowledgeBaseCF];
      }
      if (customFieldsFilterScope.includes(customFieldTypes.parts)) {
        allCustomFieldFilters = [...allCustomFieldFilters, ...partsCF];
      }
      if (customFieldsFilterScope.includes(customFieldTypes.contacts)) {
        allCustomFieldFilters = [...allCustomFieldFilters, ...contactsCF];
      }

      return allCustomFieldFilters;
    }
    return [];
  };

  const handleClearAllFilters = () => {
    if (persistFilters) {
      localStorage.setItem(selectedFilterKey, JSON.stringify([]));
      localStorage.setItem(activeFilterKey, JSON.stringify({}));
    }
    const defaultFilters = getDefaultFilters(
      scope,
      isTeamsPaid,
      isInventoryStockPaid,
    );
    const allCustomFieldFilters = getCustomFieldFilters();
    setActiveFilters({
      ...getInitialActiveFilters(scope),
      ...getValidActiveFilters({}, allCustomFieldFilters),
    });
    setSelectedFilters(() => ({ value: [] }));
    setUnselectedFilters(() => ({
      value: [...allCustomFieldFilters, ...defaultFilters],
    }));
  };

  // custom and additional fields work
  React.useEffect(() => {
    let isMount = true;

    const filteredFacilitiesCustomAdditionalFields =
      facilitiesCustomAdditionalFields?.filter(
        (field) => field?.fieldType !== "address",
      );

    if (
      (machinesCustomAdditionalFields.length > 0 ||
        filteredFacilitiesCustomAdditionalFields.length > 0 ||
        ticketsCustomAdditionalFields.length > 0 ||
        supportTicketsCustomAdditionalFields.length > 0 ||
        knowledgeBaseCustomAdditionalFields.length > 0 ||
        partsCustomAdditionalFields.length > 0 ||
        contactsCustomAdditionalFields.length > 0) &&
      !customAdditionalFieldsLoading &&
      isMount
    ) {
      const allCustomFieldFilters = getCustomFieldFilters();

      const selectedFilterIds = updateSelectedFilters(allCustomFieldFilters);

      updateUnselectedFilters(allCustomFieldFilters, selectedFilterIds);
      updateActiveFilters(allCustomFieldFilters);
      updateDropdownToggles(allCustomFieldFilters);
    }

    return () => {
      isMount = false;
    };
  }, [
    machinesCustomAdditionalFields.length,
    facilitiesCustomAdditionalFields.length,
    ticketsCustomAdditionalFields.length,
    supportTicketsCustomAdditionalFields.length,
    knowledgeBaseCustomAdditionalFields.length,
    partsCustomAdditionalFields.length,
    contactsCustomAdditionalFields.length,
  ]);

  return {
    selectedFilters,
    unselectedFilters,
    activeFilters,
    toggleFilters,
    handleSelectFilter,
    handleUnselectFilter,
    handleClearAllFilters,
    handleActiveFilter,
    applyViewFilters,
    setToggleFilters,
    scope,
  };
};

export default useFilter;
